'use client';

import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { updateEsignDocsEvent } from '@nx-frontend/shared/store/esign/esign-docs';
import { $settings } from '@nx-frontend/shared/store/portal-settings';
import { getBgClassNames } from '@nx-frontend/shared/themes';
import { PortalBgType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { FileManagerHandlers } from '../model';
import FilesManagerHeader from './files-manager-header';
import FilesManagerList from './files-manager-list';

interface FileManagerDashboardProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  title: string;
  orgRole: string;
  userId: number;
  fileOpenHost: string;
  context: {
    sessionId: string | null;
    portalId: string;
    workspaceId: string;
  };
  canEditOtherUsersFile: boolean;
  canCreateEsign: boolean;
  canSubmitEsign: boolean;
  isCustomizerMode?: boolean;
  isUploadBtnVisible?: boolean;
  handlers?: FileManagerHandlers;
  hideHeader?: boolean;
  allowedTargets?: string[];
  personalizationUserId?: number | null;
}

export const FilesManagerDashboard = ({
  themeColors,
  themeStyle,
  title,
  orgRole,
  userId,
  fileOpenHost,
  context,
  canEditOtherUsersFile,
  canCreateEsign,
  canSubmitEsign,
  isCustomizerMode,
  isUploadBtnVisible,
  handlers,
  hideHeader,
  allowedTargets,
  personalizationUserId,
}: FileManagerDashboardProps) => {
  const [updateEsignDocs, settings] = useUnit([updateEsignDocsEvent, $settings]);
  const { toast } = useToast();
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (settings && settings.portalId) {
      intervalId = setInterval(() => {
        updateEsignDocs({ portalId: settings.portalId });
      }, 3e4);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [settings, updateEsignDocs]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const title = window.localStorage.getItem('fm-toast');
      if (title) {
        window.localStorage.removeItem('fm-toast');
        setTimeout(() => {
          toast({ title });
        }, 1e3);
      }
    }
  }, [toast]);

  return (
    <div className={classNames('rounded-xl flex flex-col', getBgClassNames(themeColors, PortalBgType.SURFACE_INNER))}>
      {!hideHeader ? (
        <FilesManagerHeader
          themeColors={themeColors}
          themeStyle={themeStyle}
          title={title}
          context={context}
          orgRole={orgRole}
          isUploadBtnVisible={isUploadBtnVisible}
        />
      ) : null}
      <div className="pt-6">
        <FilesManagerList
          themeColors={themeColors}
          themeStyle={themeStyle}
          orgRole={orgRole}
          userId={userId}
          fileOpenHost={fileOpenHost}
          canEditOtherUsersFile={canEditOtherUsersFile}
          canCreateEsign={canCreateEsign}
          canSubmitEsign={canSubmitEsign}
          handlers={handlers}
          allowedTargets={allowedTargets}
          personalizationUserId={personalizationUserId}
          isCustomizerMode={isCustomizerMode}
        />
      </div>
    </div>
  );
};
